import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import locator from 'utils/locator';

import { KEY_ESCAPE } from 'constants/keys';
import { MAX_SEARCH_WORDS, MAX_SEARCH_CHARACTERS } from 'constants/search';
import SETTINGS from 'constants/settings';

import { sanitizeString, removeDiacriticsFromLetters } from 'utils/format';
import { limitWords } from 'utils/limit-words';
import { limitCharacters } from 'utils/limit-characters';
import { slowlyScrollPageVerticallyToTop } from 'utils/scroll';

import {
  RESET_CURRENT_RECIPES_PAGE,
  RESET_FILTERED_SORTED_RECIPES,
} from 'redux/modules/cms-hybrid-recipes-page/actions/types';
import { yieldToMain } from 'utils/yield-to-main';
import { useWtrDispatch } from 'redux/hooks';
import { getURLParamValue } from 'redux/modules/recipes/utils/get-url-param-value';

import { getMinSuggestChars } from 'components/Search/utils/get-min-suggest-chars';
import { saveSearchTerm } from 'components/Search/SearchForm/save-search-term';
import { getFeatureFlags } from 'utils/feature-flags';

type UpdateSearchTermArgs = {
  searchTerm: string;
  origin: string;
};

interface UseSearchFormProps {
  autoSuggestActive: boolean;
  eligibleForAutoSuggest: boolean;
  functionalCookieConsent: boolean;
  initialValue: string;
  onChange: (searchTerm: string) => (dispatch: WtrDispatch) => void;
  onClear: () => { type: string };
  onSubmit: (
    args: UpdateSearchTermArgs,
  ) => (dispatch: WtrDispatch, getState: WtrGetState) => string;
  onSearch: (() => WtrAction) | null;
  recipes?: boolean;
  url?: (searchTerm: string) => string;
  searchType?: string;
}

export const useSearchForm = ({
  autoSuggestActive,
  eligibleForAutoSuggest,
  functionalCookieConsent,
  initialValue,
  onChange,
  onClear,
  onSearch,
  onSubmit,
  recipes,
  url,
  searchType,
}: UseSearchFormProps) => {
  const dispatch = useWtrDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const insideRef = useRef<HTMLFormElement>(null);
  const { EoS_searchHardReload: searchHardReload } = getFeatureFlags();

  const [hasFocus, setHasFocus] = useState(false);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [value, setValue] = useState(initialValue);
  const hasValue = !!value?.length;
  const navigate = useNavigate();

  const focusInput = useCallback((focus: boolean) => {
    const { current: input } = inputRef;
    if (!input) return;

    if (focus) input.focus();
    else input.blur();
  }, []);

  const handleChange = useCallback(
    async (event: { target: { value: unknown } }) => {
      const eventValue = event.target.value;
      const searchTerm = limitCharacters(
        limitWords(sanitizeString(eventValue || '', true), MAX_SEARCH_WORDS),
        MAX_SEARCH_CHARACTERS,
      );
      setValue(searchTerm);

      await yieldToMain();

      const isSameSearchTerm = value === searchTerm;
      const minimumCharacters = getMinSuggestChars;
      setShowSearchHistory(searchTerm.length === 0);

      if (
        eligibleForAutoSuggest &&
        searchTerm.trim().length >= minimumCharacters &&
        !isSameSearchTerm &&
        onChange
      ) {
        dispatch(onChange(removeDiacriticsFromLetters(searchTerm)));
      }

      if (searchTerm.length < 2 && autoSuggestActive && onClear) dispatch(onClear());
    },
    [autoSuggestActive, dispatch, eligibleForAutoSuggest, onChange, onClear, value],
  );

  const handleFocus = useCallback(() => {
    if (!hasFocus) {
      focusInput(true);
      setHasFocus(true);
      setShowSearchHistory(!hasValue);
    }
  }, [focusInput, hasFocus, hasValue]);

  const handleBlur = useCallback(() => {
    if (hasFocus) {
      focusInput(false);
      setHasFocus(false);
      setShowSearchHistory(false);
    }
  }, [focusInput, hasFocus]);

  const handleClear = useCallback(
    (event: { preventDefault: () => void }) => {
      if (event) event.preventDefault();
      focusInput(true);
      if (onClear) dispatch(onClear());
      setShowSearchHistory(true);
      setValue('');
      setHasFocus(true);
    },
    [focusInput, dispatch, onClear],
  );

  const handleKeyDown = useCallback(
    (event: { keyCode: number }) => {
      if (event.keyCode === KEY_ESCAPE) {
        handleBlur();
      }
    },
    [handleBlur],
  );

  const onRecipesSubmit = useCallback(
    ({ searchTerm }: { searchTerm: string }) => {
      if (searchTerm !== getURLParamValue('searchTerm')) {
        dispatch({
          type: RESET_CURRENT_RECIPES_PAGE,
        });
        dispatch({
          type: RESET_FILTERED_SORTED_RECIPES,
        });
      }

      return encodeURIComponent(searchTerm).replace(/%20/g, '+').toString();
    },
    [dispatch],
  );

  const searchForTerm = useCallback(
    async ({ searchTerm, origin }: UpdateSearchTermArgs) => {
      const trimmedTerm = searchTerm.trim();
      const queryString = recipes
        ? onRecipesSubmit({ searchTerm })
        : dispatch(
            onSubmit({
              searchTerm: removeDiacriticsFromLetters(trimmedTerm),
              origin,
            }),
          );

      setValue(trimmedTerm);

      await yieldToMain();

      if (trimmedTerm !== '') {
        if (onClear) dispatch(onClear());
        const searchParams = new URLSearchParams(locator.search);
        const hasStubFlag =
          searchParams.get('usestubs') === 'true' || !!searchParams.get('stubflag');

        // temporary solution to avoid a full page reload on functional tests, until we fully migrate to to MFE
        if (searchHardReload && !recipes && !hasStubFlag) {
          window.location.replace(url ? url(queryString) : `${SETTINGS.searchPath}?${queryString}`);
        } else {
          navigate(url ? url(queryString) : `${SETTINGS.searchPath}?${queryString}`);
        }

        slowlyScrollPageVerticallyToTop();
      }

      handleBlur();

      if (functionalCookieConsent) {
        saveSearchTerm(trimmedTerm, searchType);
      }

      if (onSearch) dispatch(onSearch());
    },
    [
      recipes,
      onRecipesSubmit,
      dispatch,
      onSubmit,
      handleBlur,
      functionalCookieConsent,
      onSearch,
      onClear,
      searchHardReload,
      url,
      navigate,
      searchType,
    ],
  );

  const handleSubmit = useCallback(
    (event: { preventDefault: () => void }) => {
      if (event) event.preventDefault();

      return initialValue !== value
        ? searchForTerm({ searchTerm: value, origin: 'typed' })
        : setHasFocus(false);
    },
    [initialValue, searchForTerm, value],
  );

  const handleSelectedSearchHistoryItem = useCallback(
    (searchTerm: string) => {
      setShowSearchHistory(false);
      setHasFocus(false);
      return initialValue !== searchTerm
        ? searchForTerm({ searchTerm, origin: 'search_history' })
        : setValue(searchTerm);
    },
    [initialValue, searchForTerm],
  );

  const handleSelectedAutoCompleteItem = useCallback(
    (searchTerm: string) => {
      if (initialValue !== searchTerm)
        return searchForTerm({ searchTerm, origin: 'search_recommendation' });
      setHasFocus(false);
      return setValue(searchTerm);
    },
    [initialValue, searchForTerm],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setValue(initialValue);
  }, [initialValue]);

  return {
    handleBlur,
    handleChange,
    handleClear,
    handleFocus,
    handleKeyDown,
    handleSelectedAutoCompleteItem,
    handleSelectedSearchHistoryItem,
    handleSubmit,
    hasFocus,
    inputRef,
    insideRef,
    setValue,
    showSearchHistory,
    value,
  };
};
